import React, { useState } from 'react'
import { Button, Space } from 'antd'
import { DeleteOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'
import { toDepartmentFilterOptions } from '@vacationtracker/shared/functions/department-options'

import IntlMessages from '../../../../util/IntlMessages'
import ImportPicker from './import-picker'
import { SegmentedToggle } from '../../../../design-system/SegmentedToggle'

interface IBulkActionsToolbarProps {
  selectedCount: number
  departments: { id: string; name: string; parentId?: string }[]
  locations: { id: string; name: string }[]
  onApplyDepartment: (departmentId: string) => void
  onApplyLocation: (locationId: string) => void
  onApplyWelcomeMessage: (value: boolean) => void
  onRemoveSelected: () => void
}

const BulkActionsToolbar = ({
  selectedCount,
  departments,
  locations,
  onApplyDepartment,
  onApplyLocation,
  onApplyWelcomeMessage,
  onRemoveSelected,
}: IBulkActionsToolbarProps): React.ReactElement | null => {
  const { formatMessage } = useIntl()
  const [bulkDepartmentId, setBulkDepartmentId] = useState<string | undefined>(undefined)
  const [bulkLocationId, setBulkLocationId] = useState<string | undefined>(undefined)
  const [bulkWelcomeMessage, setBulkWelcomeMessage] = useState<boolean | undefined>(undefined)

  if (selectedCount === 0) {
    return null
  }

  const handleApply = () => {
    if (bulkDepartmentId) {
      onApplyDepartment(bulkDepartmentId)
    }
    if (bulkLocationId) {
      onApplyLocation(bulkLocationId)
    }
    if (bulkWelcomeMessage !== undefined) {
      onApplyWelcomeMessage(bulkWelcomeMessage)
    }
    setBulkDepartmentId(undefined)
    setBulkLocationId(undefined)
    setBulkWelcomeMessage(undefined)
  }

  const hasSelection = bulkDepartmentId || bulkLocationId || bulkWelcomeMessage !== undefined

  return (
    <div className="import-bulk-toolbar">
      <Space wrap>
        <span>
          {selectedCount} <IntlMessages id="app.selected" />
        </span>

        {departments.length > 1 && (
          <ImportPicker
            id="bulk-department"
            options={toDepartmentFilterOptions(departments)}
            value={bulkDepartmentId}
            placeholder={formatMessage({ id: 'app.department' })}
            onChange={setBulkDepartmentId}
          />
        )}

        {locations.length > 1 && (
          <ImportPicker
            id="bulk-location"
            options={locations.map((location) => ({ value: location.id, label: location.name }))}
            value={bulkLocationId}
            placeholder={formatMessage({ id: 'app.location' })}
            onChange={setBulkLocationId}
          />
        )}

        <Space>
          <IntlMessages id="components.importUsersForm.sendWelcomeMessageLabel" />
          <SegmentedToggle
            size="mini"
            options={[
              { value: true, label: formatMessage({ id: 'app.yes' }) },
              { value: false, label: formatMessage({ id: 'app.no' }) },
            ]}
            value={bulkWelcomeMessage}
            onChange={(next) => setBulkWelcomeMessage(next === true)}
          />
        </Space>

        <Button type="primary" size="small" disabled={!hasSelection} onClick={handleApply}>
          <IntlMessages id="components.importUsersForm.applyToSelected" />
        </Button>
      </Space>

      <Button danger icon={<DeleteOutlined />} onClick={onRemoveSelected}>
        <IntlMessages id="app.delete" />
      </Button>
    </div>
  )
}

export default BulkActionsToolbar
